import { useMemo, useState } from 'react';
import {
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import Form from './Form';
import {
    ServiceProfileTable,
    makeServiceNameColumn,
    makeServiceTypeColumn,
    makeVisibleScopeColumn,
} from './tableUtils';

const PLATFORM_COLUMNS = [
    makeServiceNameColumn(),
    makeServiceTypeColumn(),
    makeVisibleScopeColumn(),
];
const VALUE_ADD_COLUMNS = [makeServiceNameColumn(), makeVisibleScopeColumn()];
const OPERATOR_PLATFORM_COLUMNS = [
    makeServiceNameColumn(),
    makeVisibleScopeColumn(),
];
import useGetAllPartnerServiceProfiles from './queries/useGetAllPartnerServiceProfiles';
import useGetPartnerServiceProfiles from './queries/useGetPartnerServiceProfiles';
import useGetAllWholesalerServiceProfiles from './queries/useGetAllWholesalerServiceProfiles';
import useGetWholesalerServiceProfiles from './queries/useGetWholesalerServiceProfiles';

import { Error } from 'components/index';
import { nullError } from 'components/error';
import config from '@/config.json';
import { mappedServiceDisplayName, isOperatorPlatform } from 'utils/utils';
import { customTabStyles, selectedTabStyles } from '@/constants';

export default function ServiceProfiles() {
    const [action, setAction] = useState('GET'); // GET | EDIT | ADD
    const [editData, setEditData] = useState(null);
    const [addFilterFn, setAddFilterFn] = useState(null);
    const [selectTitle, setSelectTitle] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [level, setLevel] = useState('partner'); // partner | wholesaler

    const dispatch = useDispatch();

    const { currentWholesaler: wholesalerID, currentPartner: partnerID } =
        useSelector((state) => state.navigationLists);
    const { darkMode } = useSelector((state) => state.settings);

    const { currentScope, requiredScope } = useSelector((state) => state.login);

    const isGet = action === 'GET';
    const partnerQuery = useGetPartnerServiceProfiles({
        partnerID,
        enabled: isGet && level === 'partner' && currentScope === 40,
    });
    const allPartnerQuery = useGetAllPartnerServiceProfiles({
        wholesalerID,
        enabled: isGet && level === 'partner' && currentScope !== 40,
    });
    const wholesalerQuery = useGetWholesalerServiceProfiles({
        wholesalerID,
        enabled: isGet && level === 'wholesaler' && currentScope === 60,
    });
    const allWholesalerQuery = useGetAllWholesalerServiceProfiles({
        enabled: isGet && level === 'wholesaler' && currentScope !== 60,
    });

    const activeQuery = useMemo(() => {
        if (level === 'partner') {
            if (currentScope === 40) {
                return partnerQuery;
            }
            return allPartnerQuery;
        }

        if (currentScope === 60) {
            return wholesalerQuery;
        }
        return allWholesalerQuery;
    }, [
        level,
        currentScope,
        partnerQuery,
        allPartnerQuery,
        wholesalerQuery,
        allWholesalerQuery,
    ]);

    const data = useMemo(() => activeQuery.data ?? [], [activeQuery.data]);
    const isLoading = activeQuery.isLoading;
    const isFetching = activeQuery.isFetching;
    const refetch = activeQuery.refetch;

    const levelDropdown = useMemo(() => {
        if (requiredScope >= 60) {
            return [
                { label: 'Partner', value: 'partner' },
                { label: 'Wholesaler', value: 'wholesaler' },
            ];
        }
        return [{ label: 'Partner', value: 'partner' }];
    }, [requiredScope]);

    const scopeList = () => {
        let scopeEnum = [];
        for (
            let value = level === 'partner' ? 20 : 60;
            value <= requiredScope;
            value += 20
        ) {
            scopeEnum.push({ value, label: config.scope.json[value] });
        }
        return scopeEnum;
    };

    const handleChangeLevel = (e) => {
        if (e === 'wholesaler') {
            dispatch({ type: 'CHANGE_MINIMUM_SCOPE', payload: 60 });
        } else {
            dispatch({ type: 'CHANGE_MINIMUM_SCOPE', payload: 40 });
        }
        setLevel(e);
    };

    // Filtered data per tab — computed before early returns to satisfy hooks rules
    const platformData = useMemo(
        () => data.filter((d) => d.serviceType !== 'VendorDefined'),
        [data],
    );
    const valueAddData = useMemo(
        () =>
            data.filter(
                (d) =>
                    d.serviceType === 'VendorDefined' &&
                    !isOperatorPlatform(d.serviceDefinition?.platformID),
            ),
        [data],
    );
    const operatorPlatformData = useMemo(
        () =>
            data.filter(
                (d) =>
                    d.serviceType === 'VendorDefined' &&
                    isOperatorPlatform(d.serviceDefinition?.platformID),
            ),
        [data],
    );

    if (!partnerID || !wholesalerID) {
        return <Error error={nullError} />;
    }

    if (action !== 'GET') {
        return (
            <Form
                mode={action}
                data={editData}
                level={level}
                handleBackButton={() => setAction('GET')}
                handleSelectServiceProfileType={(v, displayName) => {
                    setEditData({
                        serviceType: v,
                        displayName: mappedServiceDisplayName(displayName),
                    });
                }}
                scopeList={scopeList}
                filterFn={addFilterFn}
                selectTitle={selectTitle}
            />
        );
    }

    const handleEdit = (rowData) => {
        setAction('EDIT');
        setEditData(rowData);
    };

    const handleAddPlatform = () => {
        setAddFilterFn(
            () => (items) =>
                items.filter((i) => !i.id.startsWith('VendorDefined')),
        );
        setSelectTitle('Select Platform Service Profile Type');
        setEditData(null);
        setAction('ADD');
    };

    const handleAddValueAdd = () => {
        setAddFilterFn(
            () => (items) =>
                items.filter((i) => i.id.startsWith('VendorDefined')),
        );
        setSelectTitle('Select Vendor Defined Profile Type');
        setEditData(null);
        setAction('ADD');
    };

    const handleAddOperatorPlatform = () => {
        setAddFilterFn(
            () => (items) =>
                items.filter((i) => i.id.startsWith('VendorDefined')),
        );
        setSelectTitle('Select Vendor Defined Profile Type');
        setEditData(null);
        setAction('ADD');
    };

    return (
        <>
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                Service Profiles
            </Text>
            <Tabs
                isLazy
                lazyBehavior="keepMounted"
                index={tabIndex}
                onChange={setTabIndex}>
                <TabList borderBottom={0} mb="10px">
                    <Tab sx={customTabStyles} _selected={selectedTabStyles}>
                        Platform Services
                    </Tab>
                    <Tab sx={customTabStyles} _selected={selectedTabStyles}>
                        Value Add Services
                    </Tab>
                    <Tab sx={customTabStyles} _selected={selectedTabStyles}>
                        Operator Platform Services
                    </Tab>
                </TabList>
                <TabPanels>
                    <TabPanel p={0}>
                        <ServiceProfileTable
                            staticColumns={PLATFORM_COLUMNS}
                            titleName="Platform Services"
                            addTooltipLabel="Add Service Profile"
                            data={platformData}
                            isLoading={isLoading}
                            isFetching={isFetching}
                            darkMode={darkMode}
                            onEdit={handleEdit}
                            onAdd={handleAddPlatform}
                            refetch={refetch}
                            level={level}
                            levelDropdown={levelDropdown}
                            onLevelChange={handleChangeLevel}
                        />
                    </TabPanel>
                    <TabPanel p={0}>
                        <ServiceProfileTable
                            staticColumns={VALUE_ADD_COLUMNS}
                            titleName="Value Add Services"
                            addTooltipLabel="Add Value Add Service Profile"
                            addButtonText="Value Add Service"
                            data={valueAddData}
                            isLoading={isLoading}
                            isFetching={isFetching}
                            darkMode={darkMode}
                            onEdit={handleEdit}
                            onAdd={handleAddValueAdd}
                            refetch={refetch}
                            level={level}
                            levelDropdown={levelDropdown}
                            onLevelChange={handleChangeLevel}
                        />
                    </TabPanel>
                    <TabPanel p={0}>
                        <ServiceProfileTable
                            staticColumns={OPERATOR_PLATFORM_COLUMNS}
                            titleName="Operator Platform Services"
                            addTooltipLabel="Add Operator Platform Service Profile"
                            addButtonText="Operator Platform Service"
                            data={operatorPlatformData}
                            isLoading={isLoading}
                            isFetching={isFetching}
                            darkMode={darkMode}
                            onEdit={handleEdit}
                            onAdd={handleAddOperatorPlatform}
                            refetch={refetch}
                            level={level}
                            levelDropdown={levelDropdown}
                            onLevelChange={handleChangeLevel}
                        />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </>
    );
}
